import { useEffect, useState } from 'react';
import {
  smartRegistry,
  type RegistryLayout,
  type Cabinet,
  type EnvironmentalSensor,
  type SpaceUtilization,
  type FileLocationResult,
  type EnvironmentalAlert,
  type CabinetType,
} from '@/services/registry';
import styles from './SmartRegistryPage.module.css';

// Mock data generators
function generateMockRegistry(): RegistryLayout {
  const cabinets: Cabinet[] = [
    {
      id: 'cab_001',
      name: 'A-01',
      type: 'standard_filing',
      position: { x: 2, y: 1, z: 0 },
      dimensions: { width: 0.9, depth: 0.6, height: 2.1 },
      shelves: [],
      status: 'available',
      totalCapacity: 200,
      usedCapacity: 156,
      utilizationPercentage: 78,
    },
    {
      id: 'cab_002',
      name: 'A-02',
      type: 'standard_filing',
      position: { x: 3, y: 1, z: 0 },
      dimensions: { width: 0.9, depth: 0.6, height: 2.1 },
      shelves: [],
      status: 'available',
      totalCapacity: 200,
      usedCapacity: 189,
      utilizationPercentage: 94,
    },
    {
      id: 'cab_003',
      name: 'A-03',
      type: 'standard_filing',
      position: { x: 4, y: 1, z: 0 },
      dimensions: { width: 0.9, depth: 0.6, height: 2.1 },
      shelves: [],
      status: 'full',
      totalCapacity: 200,
      usedCapacity: 200,
      utilizationPercentage: 100,
    },
    {
      id: 'cab_004',
      name: 'B-01',
      type: 'lateral_filing',
      position: { x: 2, y: 4, z: 0 },
      dimensions: { width: 1.2, depth: 0.5, height: 1.3 },
      shelves: [],
      status: 'available',
      totalCapacity: 150,
      usedCapacity: 67,
      utilizationPercentage: 45,
    },
    {
      id: 'cab_005',
      name: 'B-02',
      type: 'lateral_filing',
      position: { x: 3.5, y: 4, z: 0 },
      dimensions: { width: 1.2, depth: 0.5, height: 1.3 },
      shelves: [],
      status: 'available',
      totalCapacity: 150,
      usedCapacity: 89,
      utilizationPercentage: 59,
    },
    {
      id: 'cab_006',
      name: 'C-01',
      type: 'fire_resistant',
      position: { x: 7, y: 2, z: 0 },
      dimensions: { width: 0.8, depth: 0.7, height: 1.8 },
      shelves: [],
      status: 'available',
      totalCapacity: 100,
      usedCapacity: 45,
      utilizationPercentage: 45,
    },
    {
      id: 'cab_007',
      name: 'D-01',
      type: 'high_security',
      position: { x: 7, y: 5, z: 0 },
      dimensions: { width: 0.8, depth: 0.7, height: 2.0 },
      shelves: [],
      status: 'available',
      totalCapacity: 80,
      usedCapacity: 23,
      utilizationPercentage: 29,
    },
    {
      id: 'cab_008',
      name: 'M-01',
      type: 'mobile_shelving',
      position: { x: 2, y: 7, z: 0 },
      dimensions: { width: 4, depth: 0.8, height: 2.2 },
      shelves: [],
      status: 'available',
      totalCapacity: 500,
      usedCapacity: 312,
      utilizationPercentage: 62,
    },
  ];

  const sensors: EnvironmentalSensor[] = [
    {
      id: 'sensor_001',
      type: 'temperature',
      position: { x: 4, y: 4, z: 2 },
      status: 'normal',
      currentReading: 20.5,
      unit: '°C',
      thresholds: { warning: 24, critical: 28 },
      lastReading: new Date(),
    },
    {
      id: 'sensor_002',
      type: 'humidity',
      position: { x: 4, y: 4, z: 2 },
      status: 'normal',
      currentReading: 48,
      unit: '%RH',
      thresholds: { warning: 60, critical: 70 },
      lastReading: new Date(),
    },
    {
      id: 'sensor_003',
      type: 'temperature',
      position: { x: 8, y: 2, z: 2 },
      status: 'warning',
      currentReading: 25.2,
      unit: '°C',
      thresholds: { warning: 24, critical: 28 },
      lastReading: new Date(),
    },
    {
      id: 'sensor_004',
      type: 'smoke',
      position: { x: 5, y: 5, z: 2.5 },
      status: 'normal',
      currentReading: 0,
      unit: 'ppm',
      thresholds: { warning: 50, critical: 100 },
      lastReading: new Date(),
    },
  ];

  return {
    id: 'registry_001',
    name: 'Main Records Registry',
    mda: 'OHCS',
    building: 'State House Annex',
    floor: 2,
    room: 'Room 204',
    dimensions: { width: 10, length: 10, height: 3 },
    cabinets,
    aisles: [
      { id: 'aisle_1', name: 'Aisle A', startPosition: { x: 1, y: 0, z: 0 }, endPosition: { x: 1, y: 10, z: 0 }, width: 1.2, isAccessible: true },
      { id: 'aisle_2', name: 'Aisle B', startPosition: { x: 6, y: 0, z: 0 }, endPosition: { x: 6, y: 10, z: 0 }, width: 1.2, isAccessible: true },
    ],
    workstations: [
      { id: 'ws_001', name: 'Checkout Desk', type: 'checkout', position: { x: 0.5, y: 0.5, z: 0 }, status: 'occupied', operatorId: 'user_001' },
      { id: 'ws_002', name: 'Scanning Station', type: 'scanning', position: { x: 9, y: 0.5, z: 0 }, status: 'available' },
    ],
    environmentalSensors: sensors,
    lastUpdated: new Date(),
  };
}

function generateMockUtilization(registry: RegistryLayout): SpaceUtilization {
  return smartRegistry.calculateSpaceUtilization(registry);
}

export function SmartRegistryPage() {
  const [registry, setRegistry] = useState<RegistryLayout | null>(null);
  const [utilization, setUtilization] = useState<SpaceUtilization | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'layout' | 'finder' | 'utilization' | 'environment'>('finder');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<FileLocationResult | null>(null);
  const [selectedCabinet, setSelectedCabinet] = useState<Cabinet | null>(null);
  const [alerts, setAlerts] = useState<EnvironmentalAlert[]>([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const mockRegistry = generateMockRegistry();
        setRegistry(mockRegistry);
        setUtilization(generateMockUtilization(mockRegistry));

        // Check for environmental alerts
        const envAlerts = smartRegistry.checkEnvironmentalConditions(mockRegistry.environmentalSensors);
        setAlerts(envAlerts);
      } catch (err) {
        console.error('Failed to fetch registry data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  function handleSearch() {
    if (!searchQuery.trim() || !registry) return;

    // Simulate finding a file
    const mockResult: FileLocationResult = {
      pfileId: 'pf_001',
      fileNumber: searchQuery.toUpperCase(),
      staffName: 'Kwame Asante',
      location: {
        building: registry.building,
        room: registry.room,
        cabinet: 'A-02',
        shelf: 'Shelf 3',
        position: 12,
      },
      coordinates: { x: 3, y: 1, z: 0.9 },
      navigationPath: [
        { instruction: 'Enter Room 204 on floor 2', direction: 'forward', landmark: 'Main entrance' },
        { instruction: 'Walk to Cabinet A-02', direction: 'forward', distance: 3.5 },
        { instruction: 'Look at Shelf 3 (eye level)', direction: 'forward' },
        { instruction: 'File is at position 12 from left', direction: 'forward' },
      ],
      estimatedRetrievalTime: 45,
      isAvailable: true,
      lastVerified: new Date(Date.now() - 3600000),
    };

    setSearchResult(mockResult);
    setActiveTab('finder');
  }

  function getCabinetColor(cabinet: Cabinet): string {
    if (cabinet.status === 'full') return 'var(--error)';
    if (cabinet.status === 'maintenance') return 'var(--warning)';
    if (cabinet.utilizationPercentage >= 90) return '#ef4444';
    if (cabinet.utilizationPercentage >= 70) return '#f59e0b';
    if (cabinet.utilizationPercentage >= 50) return '#10b981';
    return '#22c55e';
  }

  function getCabinetTypeIcon(type: CabinetType): string {
    switch (type) {
      case 'standard_filing': return '📁';
      case 'lateral_filing': return '📂';
      case 'mobile_shelving': return '📚';
      case 'fire_resistant': return '🔥';
      case 'high_security': return '🔒';
      default: return '📁';
    }
  }

  function getSensorIcon(type: string): string {
    switch (type) {
      case 'temperature': return '🌡️';
      case 'humidity': return '💧';
      case 'smoke': return '🔥';
      case 'water_leak': return '💦';
      default: return '📡';
    }
  }

  function getSensorStatusColor(status: string): string {
    switch (status) {
      case 'normal': return 'var(--success)';
      case 'warning': return 'var(--warning)';
      case 'critical': return 'var(--error)';
      case 'offline': return 'var(--text-muted)';
      default: return 'var(--text-muted)';
    }
  }

  if (loading || !registry) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span>Loading Smart Registry...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerBadge}>File Location System</div>
          <h1 className={styles.title}>File Finder & Registry</h1>
          <p className={styles.subtitle}>
            Locate physical files • {registry.building}, {registry.room}
          </p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.searchBox}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search file number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className={styles.searchButton} onClick={handleSearch}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className={styles.alertsBanner}>
          <div className={styles.alertIcon}>⚠️</div>
          <div className={styles.alertContent}>
            <strong>{alerts.length} Environmental Alert{alerts.length > 1 ? 's' : ''}</strong>
            <span>{alerts[0].message}</span>
          </div>
          <button className={styles.alertAction} onClick={() => setActiveTab('environment')}>
            View All
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        {[
          { id: 'layout', label: 'Floor Plan', icon: '🗺️' },
          { id: 'finder', label: 'File Finder', icon: '🔍' },
          { id: 'utilization', label: 'Space Usage', icon: '📊' },
          { id: 'environment', label: 'Environment', icon: '🌡️', badge: alerts.length > 0 ? alerts.length : undefined },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.badge && <span className={styles.tabBadge}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Layout Tab - 2D Floor Plan */}
      {activeTab === 'layout' && (
        <div className={styles.content}>
          <div className={styles.layoutContainer}>
            {/* Floor Plan */}
            <div className={styles.floorPlan}>
              <div className={styles.floorPlanHeader}>
                <h3>Floor Plan View</h3>
                <div className={styles.floorPlanLegend}>
                  <span><span className={styles.legendDot} style={{ background: '#22c55e' }} /> &lt;50%</span>
                  <span><span className={styles.legendDot} style={{ background: '#10b981' }} /> 50-70%</span>
                  <span><span className={styles.legendDot} style={{ background: '#f59e0b' }} /> 70-90%</span>
                  <span><span className={styles.legendDot} style={{ background: '#ef4444' }} /> &gt;90%</span>
                </div>
              </div>

              <div className={styles.floorPlanGrid}>
                {/* Room outline */}
                <div className={styles.roomOutline}>
                  <div className={styles.roomLabel}>
                    {registry.dimensions.width}m × {registry.dimensions.length}m
                  </div>

                  {/* Entrance */}
                  <div className={styles.entrance}>
                    <span>Entrance</span>
                  </div>

                  {/* Cabinets */}
                  {registry.cabinets.map((cabinet) => (
                    <div
                      key={cabinet.id}
                      className={`${styles.cabinetMarker} ${selectedCabinet?.id === cabinet.id ? styles.selected : ''}`}
                      style={{
                        left: `${(cabinet.position.x / registry.dimensions.width) * 100}%`,
                        top: `${(cabinet.position.y / registry.dimensions.length) * 100}%`,
                        width: `${(cabinet.dimensions.width / registry.dimensions.width) * 100}%`,
                        height: `${(cabinet.dimensions.depth / registry.dimensions.length) * 100}%`,
                        backgroundColor: getCabinetColor(cabinet),
                      }}
                      onClick={() => setSelectedCabinet(cabinet)}
                      title={`${cabinet.name} - ${cabinet.utilizationPercentage}% full`}
                    >
                      <span className={styles.cabinetLabel}>{cabinet.name}</span>
                    </div>
                  ))}

                  {/* Workstations */}
                  {registry.workstations.map((ws) => (
                    <div
                      key={ws.id}
                      className={styles.workstationMarker}
                      style={{
                        left: `${(ws.position.x / registry.dimensions.width) * 100}%`,
                        top: `${(ws.position.y / registry.dimensions.length) * 100}%`,
                      }}
                      title={ws.name}
                    >
                      {ws.type === 'checkout' ? '🖥️' : ws.type === 'scanning' ? '📷' : '💻'}
                    </div>
                  ))}

                  {/* Sensors */}
                  {registry.environmentalSensors.map((sensor) => (
                    <div
                      key={sensor.id}
                      className={styles.sensorMarker}
                      style={{
                        left: `${(sensor.position.x / registry.dimensions.width) * 100}%`,
                        top: `${(sensor.position.y / registry.dimensions.length) * 100}%`,
                        borderColor: getSensorStatusColor(sensor.status),
                      }}
                      title={`${sensor.type}: ${sensor.currentReading}${sensor.unit}`}
                    >
                      {getSensorIcon(sensor.type)}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Cabinet Details Panel */}
            <div className={styles.detailsPanel}>
              {selectedCabinet ? (
                <>
                  <div className={styles.panelHeader}>
                    <span className={styles.cabinetIcon}>{getCabinetTypeIcon(selectedCabinet.type)}</span>
                    <h3>{selectedCabinet.name}</h3>
                    <span
                      className={styles.statusBadge}
                      style={{ backgroundColor: `${getCabinetColor(selectedCabinet)}20`, color: getCabinetColor(selectedCabinet) }}
                    >
                      {selectedCabinet.status}
                    </span>
                  </div>

                  <div className={styles.cabinetDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Type</span>
                      <span className={styles.detailValue}>{selectedCabinet.type.replace('_', ' ')}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Position</span>
                      <span className={styles.detailValue}>
                        ({selectedCabinet.position.x}m, {selectedCabinet.position.y}m)
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Dimensions</span>
                      <span className={styles.detailValue}>
                        {selectedCabinet.dimensions.width}×{selectedCabinet.dimensions.depth}×{selectedCabinet.dimensions.height}m
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Capacity</span>
                      <span className={styles.detailValue}>
                        {selectedCabinet.usedCapacity} / {selectedCabinet.totalCapacity} files
                      </span>
                    </div>
                  </div>

                  <div className={styles.utilizationBar}>
                    <div className={styles.utilizationHeader}>
                      <span>Utilization</span>
                      <span>{selectedCabinet.utilizationPercentage}%</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{
                          width: `${selectedCabinet.utilizationPercentage}%`,
                          backgroundColor: getCabinetColor(selectedCabinet),
                        }}
                      />
                    </div>
                  </div>

                  <div className={styles.cabinetActions}>
                    <button className={styles.actionButton}>View Files</button>
                    <button className={styles.actionButton}>Add File</button>
                  </div>
                </>
              ) : (
                <div className={styles.noSelection}>
                  <span className={styles.noSelectionIcon}>📦</span>
                  <p>Select a cabinet on the floor plan to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Finder Tab */}
      {activeTab === 'finder' && (
        <div className={styles.content}>
          <div className={styles.finderContainer}>
            {/* Search Section */}
            <div className={styles.finderSearch}>
              <h3>Find a File</h3>
              <div className={styles.searchForm}>
                <input
                  type="text"
                  className={styles.finderInput}
                  placeholder="Enter file number (e.g., OHCS-2024-001)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button className={styles.primaryButton} onClick={handleSearch}>
                  Search
                </button>
              </div>
            </div>

            {/* Search Result */}
            {searchResult ? (
              <div className={styles.searchResultCard}>
                <div className={styles.resultHeader}>
                  <div className={styles.resultBadge}>File Located</div>
                  <span className={styles.resultTime}>
                    Est. retrieval: {searchResult.estimatedRetrievalTime}s
                  </span>
                </div>

                <div className={styles.resultContent}>
                  <div className={styles.fileInfo}>
                    <h4 className={styles.fileNumber}>{searchResult.fileNumber}</h4>
                    <p className={styles.staffName}>{searchResult.staffName}</p>
                    <span className={`${styles.availabilityBadge} ${searchResult.isAvailable ? styles.available : styles.unavailable}`}>
                      {searchResult.isAvailable ? '✓ Available' : '✗ Checked Out'}
                    </span>
                  </div>

                  <div className={styles.locationInfo}>
                    <h5>Location</h5>
                    <div className={styles.locationDetails}>
                      <div className={styles.locationItem}>
                        <span className={styles.locationIcon}>🏢</span>
                        <span>{searchResult.location.building}</span>
                      </div>
                      <div className={styles.locationItem}>
                        <span className={styles.locationIcon}>🚪</span>
                        <span>{searchResult.location.room}</span>
                      </div>
                      <div className={styles.locationItem}>
                        <span className={styles.locationIcon}>📦</span>
                        <span>Cabinet {searchResult.location.cabinet}</span>
                      </div>
                      <div className={styles.locationItem}>
                        <span className={styles.locationIcon}>📚</span>
                        <span>{searchResult.location.shelf}, Position {searchResult.location.position}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Steps */}
                {searchResult.navigationPath && (
                  <div className={styles.navigationSteps}>
                    <h5>Navigation</h5>
                    <ol className={styles.stepsList}>
                      {searchResult.navigationPath.map((step, index) => (
                        <li key={index} className={styles.step}>
                          <span className={styles.stepNumber}>{index + 1}</span>
                          <span className={styles.stepInstruction}>{step.instruction}</span>
                          {step.distance && (
                            <span className={styles.stepDistance}>{step.distance.toFixed(1)}m</span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className={styles.resultActions}>
                  <button className={styles.primaryButton}>Request Retrieval</button>
                  <button className={styles.secondaryButton}>Print Location</button>
                  <button className={styles.secondaryButton} onClick={() => setActiveTab('layout')}>
                    View on Map
                  </button>
                </div>

                <div className={styles.resultMeta}>
                  Last verified: {searchResult.lastVerified.toLocaleString()}
                </div>
              </div>
            ) : (
              <div className={styles.finderEmpty}>
                <span className={styles.emptyIcon}>🔍</span>
                <h4>Search for a File</h4>
                <p>Enter a file number above to locate it in the registry</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Utilization Tab */}
      {activeTab === 'utilization' && utilization && (
        <div className={styles.content}>
          {/* Summary Cards */}
          <div className={styles.utilizationSummary}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>📊</div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryValue}>{utilization.utilizationPercentage}%</span>
                <span className={styles.summaryLabel}>Overall Utilization</span>
              </div>
              <div
                className={styles.summaryIndicator}
                style={{ backgroundColor: utilization.utilizationPercentage >= 90 ? 'var(--error)' : utilization.utilizationPercentage >= 70 ? 'var(--warning)' : 'var(--success)' }}
              />
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>📁</div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryValue}>{utilization.usedCapacity.toLocaleString()}</span>
                <span className={styles.summaryLabel}>Files Stored</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>📦</div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryValue}>{utilization.availableCapacity.toLocaleString()}</span>
                <span className={styles.summaryLabel}>Available Slots</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>📅</div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryValue}>
                  {utilization.projectedFullDate
                    ? utilization.projectedFullDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
                    : 'N/A'}
                </span>
                <span className={styles.summaryLabel}>Projected Full</span>
              </div>
            </div>
          </div>

          {/* Cabinet Utilization Grid */}
          <div className={styles.utilizationSection}>
            <h3 className={styles.sectionTitle}>Cabinet Utilization</h3>
            <div className={styles.cabinetGrid}>
              {registry.cabinets.map((cabinet) => (
                <div key={cabinet.id} className={styles.cabinetUtilCard}>
                  <div className={styles.cabinetUtilHeader}>
                    <span className={styles.cabinetUtilIcon}>{getCabinetTypeIcon(cabinet.type)}</span>
                    <span className={styles.cabinetUtilName}>{cabinet.name}</span>
                    <span
                      className={styles.cabinetUtilPercent}
                      style={{ color: getCabinetColor(cabinet) }}
                    >
                      {cabinet.utilizationPercentage}%
                    </span>
                  </div>
                  <div className={styles.cabinetUtilBar}>
                    <div
                      className={styles.cabinetUtilFill}
                      style={{
                        width: `${cabinet.utilizationPercentage}%`,
                        backgroundColor: getCabinetColor(cabinet),
                      }}
                    />
                  </div>
                  <div className={styles.cabinetUtilMeta}>
                    {cabinet.usedCapacity} / {cabinet.totalCapacity} files
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Type Breakdown */}
          <div className={styles.utilizationSection}>
            <h3 className={styles.sectionTitle}>By Cabinet Type</h3>
            <div className={styles.typeBreakdown}>
              {(Object.entries(utilization.byType) as [CabinetType, { total: number; used: number; percentage: number }][])
                .filter(([, data]) => data.total > 0)
                .map(([type, data]) => (
                  <div key={type} className={styles.typeCard}>
                    <div className={styles.typeHeader}>
                      <span className={styles.typeIcon}>{getCabinetTypeIcon(type)}</span>
                      <span className={styles.typeName}>{type.replace('_', ' ')}</span>
                    </div>
                    <div className={styles.typeStats}>
                      <span className={styles.typePercent}>{data.percentage}%</span>
                      <span className={styles.typeCount}>{data.used} / {data.total}</span>
                    </div>
                    <div className={styles.typeBar}>
                      <div
                        className={styles.typeFill}
                        style={{ width: `${data.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Recommendations */}
          {utilization.recommendations.length > 0 && (
            <div className={styles.recommendationsSection}>
              <h3 className={styles.sectionTitle}>Recommendations</h3>
              <ul className={styles.recommendationsList}>
                {utilization.recommendations.map((rec, index) => (
                  <li key={index} className={styles.recommendation}>
                    <span className={styles.recIcon}>💡</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Environment Tab */}
      {activeTab === 'environment' && (
        <div className={styles.content}>
          {/* Optimal Conditions Banner */}
          <div className={styles.optimalBanner}>
            <h4>Optimal Storage Conditions</h4>
            <div className={styles.optimalConditions}>
              <div className={styles.optimalItem}>
                <span className={styles.optimalIcon}>🌡️</span>
                <span>Temperature: 18-22°C</span>
              </div>
              <div className={styles.optimalItem}>
                <span className={styles.optimalIcon}>💧</span>
                <span>Humidity: 35-55% RH</span>
              </div>
            </div>
          </div>

          {/* Current Readings */}
          <div className={styles.sensorGrid}>
            {registry.environmentalSensors.map((sensor) => (
              <div key={sensor.id} className={styles.sensorCard}>
                <div className={styles.sensorHeader}>
                  <span className={styles.sensorIcon}>{getSensorIcon(sensor.type)}</span>
                  <span className={styles.sensorType}>{sensor.type.replace('_', ' ')}</span>
                  <span
                    className={styles.sensorStatus}
                    style={{ backgroundColor: `${getSensorStatusColor(sensor.status)}20`, color: getSensorStatusColor(sensor.status) }}
                  >
                    {sensor.status}
                  </span>
                </div>
                <div className={styles.sensorReading}>
                  <span className={styles.readingValue}>{sensor.currentReading}</span>
                  <span className={styles.readingUnit}>{sensor.unit}</span>
                </div>
                <div className={styles.sensorThresholds}>
                  <div className={styles.threshold}>
                    <span className={styles.thresholdLabel}>Warning</span>
                    <span className={styles.thresholdValue}>{sensor.thresholds.warning}{sensor.unit}</span>
                  </div>
                  <div className={styles.threshold}>
                    <span className={styles.thresholdLabel}>Critical</span>
                    <span className={styles.thresholdValue}>{sensor.thresholds.critical}{sensor.unit}</span>
                  </div>
                </div>
                <div className={styles.sensorMeta}>
                  Last reading: {sensor.lastReading.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>

          {/* Active Alerts */}
          {alerts.length > 0 && (
            <div className={styles.alertsSection}>
              <h3 className={styles.sectionTitle}>Active Alerts</h3>
              <div className={styles.alertsList}>
                {alerts.map((alert) => (
                  <div key={alert.id} className={`${styles.alertCard} ${styles[alert.severity]}`}>
                    <div className={styles.alertHeader}>
                      <span className={styles.alertSeverity}>
                        {alert.severity === 'critical' ? '🚨' : '⚠️'} {alert.severity}
                      </span>
                      <span className={styles.alertTime}>
                        {alert.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                    <p className={styles.alertMessage}>{alert.message}</p>
                    <div className={styles.alertDetails}>
                      <span>Reading: {alert.reading}</span>
                      <span>Threshold: {alert.threshold}</span>
                    </div>
                    <div className={styles.alertActions}>
                      <button className={styles.acknowledgeButton}>Acknowledge</button>
                      <button className={styles.viewButton}>View Sensor</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {alerts.length === 0 && (
            <div className={styles.noAlerts}>
              <span className={styles.noAlertsIcon}>✓</span>
              <h4>All Systems Normal</h4>
              <p>No environmental alerts at this time</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
